<template>
  <div class="comment-view">
    <!-- 본글 댓글 입력 -->
    <div class="comment-input">
      <textarea
        v-model="edit_comment.content"
        placeholder="댓글을 입력하세요..."
        @keydown.ctrl.enter="saveComment"
      ></textarea>
      <button class="sticker-btn" @click="showSticker = true">
        <i class="ph ph-sticker"></i>
      </button>
      <button @click="saveComment">
        <i class="ph ph-arrow-right"></i>
      </button>
    </div>
    <!-- 선택된 스티커 미리보기 -->
    <div v-if="edit_comment.sticker" class="sticker-preview">
      <img :src="edit_comment.sticker" />
      <span class="sticker-remove" @click="edit_comment.sticker = ''">&times;</span>
    </div>

    <StickerSelector v-model="showSticker" @select="onStickerSelect" />

    <ul class="comment-list">
      <li
        v-for="comment in comments"
        :key="comment.idx"
        :style="{ 'padding-left': comment.depth * 24 + 'px' }"
      >
        <img :src="comment.profile.image" :alt="comment.profile.nickname" />
        
        <div class="content">
          <span>{{ comment.profile.nickname }}</span>
          
          <!-- 일반 모드 -->
          <template v-if="!comment.is_editing">
            <p>{{ comment.content }}</p>
            <img v-if="comment.sticker" :src="comment.sticker" class="comment-sticker" />
            <div class="status">
              <template v-if="isOwner(comment)">
                <span class="clickable" @click="editComment(comment)">수정</span>
                <span class="clickable" @click="deleteComment(comment.idx)">삭제</span>
              </template>
              <span class="clickable" @click="doReply(comment)">답글</span>
              <span>{{ comment.created_at }}</span>
            </div>
          </template>

          <!-- 수정 모드 -->
          <template v-else>
            <div class="edit-pack">
              <input 
                v-model="edit_comment.content" 
                @keydown.enter="saveComment"
                @keydown.esc="cancelEdit(comment)"
              />
            </div>
            <div class="status">
              <span class="clickable" @click="cancelEdit(comment)">취소</span>
              <span class="clickable" @click="saveComment">저장</span>
            </div>
          </template>

          <!-- 답글 입력 -->
          <div v-if="comment.is_replying" class="reply-input">
            <i class="ph ph-arrow-elbow-down-right"></i>
            <textarea
              v-model="edit_comment.content"
              placeholder="답글을 입력하세요..."
              @keydown.ctrl.enter="saveComment"
              @keydown.esc="cancelReply(comment)"
            ></textarea>
            <button class="sticker-btn" @click="showSticker = true">
              <i class="ph ph-sticker"></i>
            </button>
            <button @click="saveComment">
              <i class="ph ph-arrow-right"></i>
            </button>
            <div v-if="edit_comment.sticker" class="sticker-preview">
              <img :src="edit_comment.sticker" />
              <span class="sticker-remove" @click="edit_comment.sticker = ''">&times;</span>
            </div>
          </div>
        </div>

        <!-- 좋아요 / 저장 버튼 -->
        <p 
          v-if="!comment.is_editing"
          @click="toggleLike(comment)"
          :style="{ color: comment.liked_by_user ? 'red' : '#888' }"
        >
          <i class="ph ph-heart"></i>
          <small>{{ comment.like_count }}</small>
        </p>
        <p v-else @click="saveComment">
          <i class="ph ph-check"></i>
        </p>
      </li>
    </ul>
  </div>
</template>

<script>
import { inject, onMounted, ref, computed } from "vue";
import StickerSelector from "@/components/dialogs/StickerSelector.vue";

export default {
  components: { StickerSelector },
  props: {
    target_table: { type: String, required: true },
    target_id: { type: [String, Number], required: true },
  },
  setup(props) {
    // Injections
    const sql_query = inject("sql_query");
    const getMbid = inject("getMbid");
    const getUserProfileInfo = inject("getUserProfileInfo");

    // State
    const comments = ref([]);
    const edit_comment = ref(createEmptyComment());
    const showSticker = ref(false);

    function onStickerSelect(url) {
      edit_comment.value.sticker = url;
    }

    // Computed
    const currentUserId = computed(() => getMbid());

    // Lifecycle
    onMounted(() => {
      fetchComments();
    });

    // Helper Functions
    function createEmptyComment() {
      return {
        idx: "",
        mb_id: getMbid(),
        target_table: props.target_table,
        target_idx: props.target_id,
        parent_idx: "",
        content: "",
        sticker: "",
        depth: "0",
      };
    }

    function resetEditComment() {
      edit_comment.value = createEmptyComment();
    }

    function isOwner(comment) {
      return comment.mb_id === currentUserId.value;
    }

    function closeAllEditModes() {
      comments.value.forEach((comment) => {
        comment.is_editing = false;
        comment.is_replying = false;
      });
    }

    // Comment CRUD Operations
    async function fetchComments() {
      const sql = `
        SELECT * 
        FROM comment 
        WHERE target_table='${props.target_table}' 
          AND target_idx='${props.target_id}'
        ORDER BY parent_idx ASC, depth ASC, created_at ASC
      `;
      const rows = await sql_query(sql);

      const enriched = await Promise.all(
        rows.map(async (comment) => {
          const [profile, like_count, liked_by_user] = await Promise.all([
            getUserProfileInfo(comment.mb_id),
            getCommentLikes(comment.idx),
            checkUserLiked(comment.idx),
          ]);
          
          return {
            ...comment,
            profile,
            like_count,
            liked_by_user,
            is_editing: false,
            is_replying: false,
          };
        })
      );

      comments.value = sortComments(enriched);
    }

    function sortComments(commentList) {
      return commentList.sort((a, b) => {
        const parentA = a.parent_idx || a.idx;
        const parentB = b.parent_idx || b.idx;
        
        if (parentA !== parentB) return parentA - parentB;
        if (a.depth !== b.depth) return a.depth - b.depth;
        return new Date(a.created_at) - new Date(b.created_at);
      });
    }

    async function saveComment() {
      const content = edit_comment.value.content.trim();
      const sticker = edit_comment.value.sticker || '';
      if (!content && !sticker) {
        alert("댓글 내용을 입력하세요.");
        return;
      }

      const isUpdate = !!edit_comment.value.idx;
      const sql = isUpdate ? buildUpdateSQL() : buildInsertSQL();

      const res = await sql_query(sql);
      if (res.affectedRows) {
        closeAllEditModes();
        resetEditComment();
        await fetchComments();
      }
    }

    function buildUpdateSQL() {
      return `
        UPDATE comment 
        SET content='${edit_comment.value.content}', sticker='${edit_comment.value.sticker || ''}' 
        WHERE idx='${edit_comment.value.idx}' AND mb_id='${currentUserId.value}'
      `;
    }

    function buildInsertSQL() {
      const parent = edit_comment.value.parent_idx
        ? `'${edit_comment.value.parent_idx}'`
        : "NULL";
      const depth = parseInt(edit_comment.value.depth) || 0;
      const sticker = edit_comment.value.sticker || '';
      
      return `
        INSERT INTO comment 
        (mb_id, target_table, target_idx, parent_idx, content, sticker, depth, status)
        VALUES (
          '${currentUserId.value}', 
          '${edit_comment.value.target_table}', 
          '${edit_comment.value.target_idx}', 
          ${parent}, 
          '${edit_comment.value.content}', 
          '${sticker}',
          ${depth}, 
          'normal'
        )
      `;
    }

    async function deleteComment(idx) {
      if (!confirm("댓글을 삭제하시겠습니까?")) return;
      
      const sql = `
        DELETE FROM comment 
        WHERE idx='${idx}' AND mb_id='${currentUserId.value}'
      `;
      const res = await sql_query(sql);
      
      if (res.affectedRows) {
        await fetchComments();
      }
    }

    function editComment(comment) {
      closeAllEditModes();
      edit_comment.value.idx = comment.idx;
      edit_comment.value.content = comment.content;
      edit_comment.value.sticker = comment.sticker;
      comment.is_editing = true;
    }

    function cancelEdit(comment) {
      comment.is_editing = false;
      resetEditComment();
    }

    // Reply Operations
    function doReply(comment) {
      closeAllEditModes();
      resetEditComment();
      edit_comment.value.parent_idx = comment.idx;
      edit_comment.value.depth = parseInt(comment.depth) + 1;
      comment.is_replying = true;
    }

    function cancelReply(comment) {
      comment.is_replying = false;
      resetEditComment();
    }

    // Like Operations
    async function toggleLike(comment) {
      const sql = comment.liked_by_user
        ? buildDeleteLikeSQL(comment.idx)
        : buildInsertLikeSQL(comment.idx);

      await sql_query(sql);
      comment.liked_by_user = !comment.liked_by_user;
      comment.like_count += comment.liked_by_user ? 1 : -1;
    }

    function buildInsertLikeSQL(comment_idx) {
      return `
        INSERT INTO likes (target_table, target_idx, mb_id)
        VALUES ('comment', '${comment_idx}', '${currentUserId.value}')
      `;
    }

    function buildDeleteLikeSQL(comment_idx) {
      return `
        DELETE FROM likes 
        WHERE target_table='comment' 
          AND target_idx='${comment_idx}' 
          AND mb_id='${currentUserId.value}'
      `;
    }

    async function getCommentLikes(comment_idx) {
      const sql = `
        SELECT COUNT(*) AS cnt 
        FROM likes 
        WHERE target_table='comment' AND target_idx='${comment_idx}'
      `;
      const rows = await sql_query(sql);
      return rows[0]?.cnt || 0;
    }

    async function checkUserLiked(comment_idx) {
      const sql = `
        SELECT COUNT(*) AS cnt
        FROM likes 
        WHERE target_table='comment' 
          AND target_idx='${comment_idx}' 
          AND mb_id='${currentUserId.value}'
      `;
      const rows = await sql_query(sql);
      return rows[0]?.cnt > 0;
    }

    return {
      comments,
      edit_comment,
      showSticker,
      getMbid,
      isOwner,
      saveComment,
      deleteComment,
      editComment,
      cancelEdit,
      toggleLike,
      doReply,
      cancelReply,
      onStickerSelect,
    };
  },
};
</script>

<style scoped>
.comment-view {
  width:100%;
  display:flex;
  flex-direction: column;
  align-items: center;
  gap:24px;
  padding-bottom:120px;
}

ul.comment-list {
  width:100%;
  max-width:var(--break-point-mobile);
  list-style: none;
  padding:0;
  margin:0;
  display:flex;
  flex-direction: column;
  gap:24px;
}

ul.comment-list li {
  display:flex;
  gap:12px;
}

ul.comment-list li > img {
  min-width: 40px;
  width:40px;
  height:40px;
  border-radius: 50%;
  background-color: #ccc;
  object-fit: cover;
}

ul.comment-list li > p {
  display:flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  cursor: pointer;
  user-select: none;
  width:40px;
  height:40px;
  border-radius:50%;
  background-color: var(--color-surface-reverse);
  font-size:16px;
}

ul.comment-list li .content {
  flex:1;
  display:flex;
  flex-direction: column;
  gap:4px;
}

ul.comment-list li .content span {
  font-size:14px;
}

ul.comment-list li .content p {
  padding:0;
  margin:0;
  font-size:16px;
}

ul.comment-list li .content .status {
  font-size: 12px;
  color: #888;
  display:flex;
  gap:8px;
}

ul.comment-list li .content .status .clickable {
  cursor: pointer;
  color: var(--color-text-primary);
  text-decoration: underline;
}

ul.comment-list li .content .edit-pack {
  display:flex;
  gap:8px;
}

ul.comment-list li .content .edit-pack input {
  flex:1;
  padding:4px 8px;
  font-size:14px;
}

.comment-input {
  width:100%;
  max-width:var(--break-point-mobile);
  display:flex;
  gap:8px;
}

.comment-input textarea {
  flex:1;
  resize:none;
  height:36px;
  font-size:14px;
  padding:8px;
  box-sizing: border-box;
  display:flex;
  align-items: center;
  border-radius: 12px;
}

.comment-input button {
  width:40px;
  height:auto;
  aspect-ratio: 1/1 auto;
  padding:0;
  margin:0;
  font-size:14px;
  cursor: pointer;
  border-radius: 50%;
  background:var(--color-surface-reverse);
  border:none;
  display:flex;
  align-items: center;
  justify-content: center;
}

.reply-input {
  display:flex;
  align-items: center;
  justify-content: center;
  padding-top:12px;
  gap:8px;
}

.reply-input textarea {
  flex:1;
  resize:none;
  height:36px;
  font-size:14px;
  padding:8px;
  box-sizing: border-box;
  display:flex;
  align-items: center;
  border-radius: 12px;
}

.reply-input button {
  width:40px;
  height:auto;
  aspect-ratio: 1/1 auto;
  padding:0;
  margin:0;
  font-size:14px;
  cursor: pointer;
  border-radius: 50%;
  background:var(--color-surface-reverse);
  border:none;
  display:flex;
  align-items: center;
  justify-content: center;
}

.sticker-btn {
  background: transparent !important;
  color: var(--color-text-secondary);
  font-size: 18px !important;
}

.sticker-preview {
  position: relative;
  display: inline-block;
  width: 80px;
}
.sticker-preview img {
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 8px;
  background: var(--color-background-light, #f5f5f5);
}
.sticker-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  background: #e74c3c;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  line-height: 1;
}

.comment-sticker {
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 8px;
  margin-top: 4px;
}

</style>